import React, { Component, createRef, Suspense, lazy } from 'react';

const ConversionLoadingOverlay = lazy(() => import(
    /* webpackChunkName: "conversion-overlay" */
    '../../../loading_animation/conversion_loading_overlay'
));

const SLIDE_TYPES = [
    { value: 'point', label: 'Point', bgClass: 'bg-blue-100', textClass: 'text-blue-700' },
    { value: 'scripture', label: 'Scripture', bgClass: 'bg-purple-100', textClass: 'text-purple-700' },
    { value: 'song', label: 'Song', bgClass: 'bg-green-100', textClass: 'text-green-700' },
];

function getTypeStyle(type) {
    const found = SLIDE_TYPES.find(t => t.value === type);
    return found || SLIDE_TYPES[0];
}

function defaultModifiers() {
    return { bold: false, italic: false, underline: false };
}

function blankSlide() {
    return {
        body: [{ text: '', modifiers: defaultModifiers() }],
        type: 'point',
        reference: null,
    };
}

class AiDraftReview extends Component {

    constructor(props) {
        super(props);
        this.state = {
            name: props.draft_name || 'Untitled',
            slides: props.draft_slides ? JSON.parse(JSON.stringify(props.draft_slides)) : [],
            addBlankSlides: true,
            showToolbar: false,
            toolbarPosition: { top: 0, left: 0 },
            activeSlideIndex: null,
            activeLineIndex: null,
            showLoadingOverlay: false,
        };
        this.containerRef = createRef();
        this.toolbarRef = createRef();
        this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleDocumentMouseDown);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.draft_slides !== this.props.draft_slides && this.props.draft_slides) {
            this.setState({
                slides: JSON.parse(JSON.stringify(this.props.draft_slides)),
                name: this.props.draft_name || 'Untitled',
            });
        }
    }

    handleDocumentMouseDown(e) {
        if (this.state.showToolbar && this.toolbarRef.current && !this.toolbarRef.current.contains(e.target)) {
            this.setState({ showToolbar: false, activeSlideIndex: null, activeLineIndex: null });
        }
    }

    handleNameChange = (e) => {
        this.setState({ name: e.target.value });
    }

    handleAddSlide = () => {
        const slides = [...this.state.slides, blankSlide()];
        this.setState({ slides });
        this.props.onUpdateDraft(slides);
    }

    handleDeleteSlide = (index) => {
        const slides = this.state.slides.filter((_, i) => i !== index);
        this.setState({ slides });
        this.props.onUpdateDraft(slides);
    }

    handleTypeChange = (index, newType) => {
        const slides = JSON.parse(JSON.stringify(this.state.slides));
        slides[index].type = newType;
        if (newType === 'scripture' && !slides[index].reference) {
            slides[index].reference = { text: '', modifiers: defaultModifiers() };
        }
        if (newType !== 'scripture') {
            slides[index].reference = null;
        }
        this.setState({ slides });
        this.props.onUpdateDraft(slides);
    }

    handleBodyLineBlur = (slideIndex, lineIndex, e) => {
        const slides = JSON.parse(JSON.stringify(this.state.slides));
        const text = e.target.innerText;
        if (slides[slideIndex] && slides[slideIndex].body && slides[slideIndex].body[lineIndex]) {
            slides[slideIndex].body[lineIndex].text = text;
            this.setState({ slides });
            this.props.onUpdateDraft(slides);
        }
    }

    handleReferenceBlur = (slideIndex, e) => {
        const slides = JSON.parse(JSON.stringify(this.state.slides));
        const text = e.target.innerText;
        if (slides[slideIndex] && slides[slideIndex].reference) {
            slides[slideIndex].reference.text = text;
            this.setState({ slides });
            this.props.onUpdateDraft(slides);
        }
    }

    handleTextSelection = (slideIndex, lineIndex, e) => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.toString().trim()) {
            return;
        }
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const containerRect = this.containerRef.current.getBoundingClientRect();
        this.setState({
            showToolbar: true,
            toolbarPosition: {
                top: rect.top - containerRect.top - 40,
                left: rect.left - containerRect.left + (rect.width / 2) - 60,
            },
            activeSlideIndex: slideIndex,
            activeLineIndex: lineIndex,
        });
    }

    handleToggleModifier = (modifier) => {
        const { activeSlideIndex, activeLineIndex } = this.state;
        if (activeSlideIndex === null || activeLineIndex === null) return;
        const slides = JSON.parse(JSON.stringify(this.state.slides));
        const line = slides[activeSlideIndex] && slides[activeSlideIndex].body && slides[activeSlideIndex].body[activeLineIndex];
        if (!line) return;
        line.modifiers = line.modifiers || defaultModifiers();
        line.modifiers[modifier] = !line.modifiers[modifier];
        this.setState({ slides });
        this.props.onUpdateDraft(slides);
    }

    handleGenerate = () => {
        this.setState({ showLoadingOverlay: true });
        const minTimer = new Promise(resolve => setTimeout(resolve, 5000));

        this.props.onGenerateFromDraft(
            this.state.name,
            this.state.slides,
            this.props.draft_images || {},
            this.state.addBlankSlides,
            (doc) => {
                minTimer.then(() => {
                    // Clear draft and navigate AFTER the min timer — avoids
                    // flashing back to the landing page before navigation.
                    if (this.props.onCancelDraft) this.props.onCancelDraft();
                    if (doc && this.props.onNavigateToDocument) {
                        this.props.onNavigateToDocument(doc.id);
                    }
                });
            }
        );
    }

    handleCancel = () => {
        this.props.onCancelDraft();
    }

    handleToggleBlankSlides = () => {
        this.setState(prev => ({ addBlankSlides: !prev.addBlankSlides }));
    }

    renderBodyLine(slide, slideIndex, line, lineIndex) {
        const mods = line.modifiers || {};
        let style = {};
        if (mods.bold) style.fontWeight = 'bold';
        if (mods.italic) style.fontStyle = 'italic';
        if (mods.underline) style.textDecoration = 'underline';

        return (
            <div
                key={lineIndex}
                contentEditable
                suppressContentEditableWarning
                className="text-gray-800 focus:outline-hidden border-b border-transparent hover:border-gray-200 px-1 py-0.5"
                style={style}
                onBlur={(e) => this.handleBodyLineBlur(slideIndex, lineIndex, e)}
                onMouseUp={(e) => this.handleTextSelection(slideIndex, lineIndex, e)}
                onKeyUp={(e) => this.handleTextSelection(slideIndex, lineIndex, e)}
            >
                {line.text}
            </div>
        );
    }

    renderImageSlide(slide, slideIndex) {
        const imageData = this.props.draft_images && this.props.draft_images[slide.image];
        return (
            <div>
                {imageData ? (
                    <img
                        src={'data:image/png;base64,' + imageData}
                        alt={slide.image}
                        className="max-h-32 object-contain bg-gray-100 rounded-sm"
                    />
                ) : (
                    <div className="max-h-32 bg-gray-100 rounded-sm flex items-center justify-center text-gray-400 p-4">
                        No preview
                    </div>
                )}
                <p className="text-xs text-gray-500 mt-1">{slide.image}</p>
            </div>
        );
    }

    renderSlideCard(slide, index) {
        const isImage = slide.image != null;
        const typeStyle = getTypeStyle(slide.type || 'point');

        return (
            <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-xs p-4 mb-3">
                {/* Card header */}
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400 font-medium">#{index + 1}</span>
                        {!isImage && (
                            <select
                                value={slide.type || 'point'}
                                onChange={(e) => this.handleTypeChange(index, e.target.value)}
                                className={'text-xs rounded-full pl-3 pr-6 py-0.5 border-none focus:outline-hidden focus:ring-0 cursor-pointer appearance-none bg-no-repeat ' + typeStyle.bgClass + ' ' + typeStyle.textClass}
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 20 20\' fill=\'currentColor\'%3E%3Cpath fill-rule=\'evenodd\' d=\'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z\' clip-rule=\'evenodd\'/%3E%3C/svg%3E")', backgroundPosition: 'right 6px center', backgroundSize: '12px' }}
                            >
                                {SLIDE_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        )}
                        {isImage && (
                            <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">Image</span>
                        )}
                    </div>
                    <button
                        onClick={() => this.handleDeleteSlide(index)}
                        className="text-gray-400 hover:text-red-500 transition text-lg leading-none"
                        title="Delete slide"
                    >
                        &times;
                    </button>
                </div>

                {/* Card body */}
                {isImage ? (
                    this.renderImageSlide(slide, index)
                ) : (
                    <div>
                        {(slide.body || []).map((line, lineIndex) =>
                            this.renderBodyLine(slide, index, line, lineIndex)
                        )}
                        {slide.type === 'scripture' && slide.reference && (
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                className="text-sm text-gray-500 italic border-b border-gray-200 mt-2 px-1 py-0.5 focus:outline-hidden"
                                onBlur={(e) => this.handleReferenceBlur(index, e)}
                            >
                                {slide.reference.text}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    renderFloatingToolbar() {
        if (!this.state.showToolbar) return null;
        const { activeSlideIndex, activeLineIndex } = this.state;
        const line = this.state.slides[activeSlideIndex] &&
            this.state.slides[activeSlideIndex].body &&
            this.state.slides[activeSlideIndex].body[activeLineIndex];
        const mods = (line && line.modifiers) || {};

        return (
            <div
                ref={this.toolbarRef}
                className="absolute bg-gray-800 text-white rounded-sm shadow-lg px-2 py-1 z-50 flex items-center space-x-1"
                style={{ top: this.state.toolbarPosition.top, left: this.state.toolbarPosition.left }}
            >
                <button
                    className={'px-2 py-1 rounded-sm hover:bg-gray-700 text-sm font-bold ' + (mods.bold ? 'bg-gray-600' : '')}
                    onMouseDown={(e) => { e.preventDefault(); this.handleToggleModifier('bold'); }}
                >
                    B
                </button>
                <button
                    className={'px-2 py-1 rounded-sm hover:bg-gray-700 text-sm italic ' + (mods.italic ? 'bg-gray-600' : '')}
                    onMouseDown={(e) => { e.preventDefault(); this.handleToggleModifier('italic'); }}
                >
                    I
                </button>
                <button
                    className={'px-2 py-1 rounded-sm hover:bg-gray-700 text-sm underline ' + (mods.underline ? 'bg-gray-600' : '')}
                    onMouseDown={(e) => { e.preventDefault(); this.handleToggleModifier('underline'); }}
                >
                    U
                </button>
            </div>
        );
    }

    render() {
        const { slides, name } = this.state;
        const { generating } = this.props;

        return (
            <div className="flex flex-col relative" style={{ minHeight: 200, height: 'calc(100vh - 200px)' }} ref={this.containerRef}>
                {/* Lottie loading overlay during generation */}
                <Suspense fallback={null}>
                    <ConversionLoadingOverlay visible={this.state.showLoadingOverlay} />
                </Suspense>

                {/* Floating toolbar */}
                {this.renderFloatingToolbar()}

                {/* Top bar */}
                <div className="p-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center justify-between mb-1">
                        <input
                            type="text"
                            className="text-lg font-medium text-gray-800 border-none focus:outline-hidden focus:ring-0 bg-transparent flex-1 mr-3"
                            value={name}
                            onChange={this.handleNameChange}
                            placeholder="Presentation name"
                        />
                        <button
                            className="inline-flex items-center px-4 py-1.5 text-sm bg-blue-500 text-white rounded-sm hover:bg-blue-600 transition whitespace-nowrap disabled:opacity-50"
                            onClick={this.handleGenerate}
                            disabled={generating}
                        >
                            {generating ? 'Generating...' : 'Generate'}
                        </button>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{slides.length} slide{slides.length !== 1 ? 's' : ''}</span>
                        <div className="flex items-center space-x-2">
                            <label className="flex items-center text-xs text-gray-500 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={this.state.addBlankSlides}
                                    onChange={this.handleToggleBlankSlides}
                                    className="mr-1"
                                />
                                Blank slides between
                            </label>
                            <button
                                className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition"
                                onClick={this.handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition"
                                onClick={this.handleAddSlide}
                            >
                                + Add
                            </button>
                        </div>
                    </div>
                </div>

                {/* Scrollable slide list */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                    {slides.map((slide, index) => this.renderSlideCard(slide, index))}
                    {slides.length === 0 && (
                        <div className="text-center text-gray-400 py-12">
                            No slides yet. Click "+ Add" to create one.
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default AiDraftReview;
